import { useState } from 'react';

import { OrgAvatar } from '../Avatar';
import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonA from '../Input2/ButtonA';
import DeletePopup from '../Popup/DeletePopup';
import { Arrows, Education, General, Maps, Time } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import AddExperiencePopup, { type AddExperienceInput, type OrgSearchOption } from './AddExperiencePopup';

export type ContactExperienceItem = {
  id: string;
  title: string | null;
  company: string | null;
  companyUrl: string | null;
  employmentType: string | null;
  dateRange: string | null;
  location: string | null;
  description: string | null;
  sortOrder: number | null;
  scrapedAt: Date;
  orgPublicId: string | null;
  orgPictureUrl: string | null;
  source: 'manual' | 'linkedin';
};

export type ContactEducationItem = {
  id: string;
  school: string | null;
  schoolId: string | null;
  degree: string | null;
  fieldOfStudy: string | null;
  grade: string | null;
  activities: string | null;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  sortOrder: number | null;
  scrapedAt: Date;
};

export type ContactEmploymentEducationSectionPureProps = {
  experiences: ContactExperienceItem[];
  education: ContactEducationItem[];
  isLoading: boolean;
  /** When provided, an "Add experience" button is shown that opens a popup for manual entry. */
  onAddExperience?: (input: AddExperienceInput) => Promise<void>;
  /** Async org search used by the add-experience popup. Required when onAddExperience is set. */
  orgSearch?: (query: string) => Promise<OrgSearchOption[]>;
  /** Removes a manually-added experience. Only shown for rows with source === 'manual'. */
  onDeleteExperience?: (experienceId: string) => Promise<void>;
  /** Used to phrase the "currently works here" label and any future contact-name strings. */
  contactFirstName?: string | null;
};

function joinNonEmpty(parts: (string | null | undefined)[], sep: string): string | null {
  const filtered = parts.filter((p): p is string => p != null && p.trim().length > 0);
  if (filtered.length === 0) return null;
  return filtered.join(sep);
}

function formatEducationDateRange(start: string | null, end: string | null): string | null {
  if (start == null && end == null) return null;
  if (start != null && end != null) return `${start} – ${end}`;
  return start ?? end;
}

function ExperienceRow({
  item,
  onDelete,
}: {
  item: ContactExperienceItem;
  onDelete?: () => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const headline = joinNonEmpty([item.title, item.company], ' · ');
  const hasLocation = item.location != null && item.location.trim().length > 0;
  const hasEmploymentType = item.employmentType != null && item.employmentType.trim().length > 0;
  const hasDescription = item.description != null && item.description.trim().length > 0;
  const hasCompanyUrl = item.companyUrl != null && item.companyUrl.trim().length > 0;
  const hasMore = hasLocation || hasEmploymentType || hasDescription || hasCompanyUrl;
  const hasOrgPicture = item.orgPictureUrl != null && item.orgPictureUrl.trim().length > 0;
  const canDelete = item.source === 'manual' && onDelete != null;

  return (
    <li className="flex items-start gap-2.5">
      {hasOrgPicture ? (
        <OrgAvatar
          src={item.orgPictureUrl}
          name={item.company ?? ''}
          className="mt-0.5 size-4 shrink-0"
          shape="square"
        />
      ) : (
        <General.Building02 className="mt-0.5 size-4 shrink-0 text-gray-400" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1.5">
          <div className="min-w-0 flex-1">
            {headline != null && (
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{headline}</div>
            )}
            {item.dateRange != null && (
              <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <Time.Calendar className="size-3 shrink-0 text-gray-400" />
                <span className="truncate">{item.dateRange}</span>
              </div>
            )}
          </div>
          {hasMore && (
            <Button
              size="sm"
              text
              icon={
                <Arrows.ChevronDown
                  className={cn('transition-transform', expanded && 'rotate-180')}
                />
              }
              tooltip={expanded ? 'Hide details' : 'Show details'}
              onClick={() => setExpanded((v) => !v)}
            />
          )}
          {canDelete && (
            <Button
              size="sm"
              text
              icon={<General.Trash01 />}
              tooltip="Remove experience"
              onClick={() => setConfirmingDelete(true)}
            />
          )}
        </div>
        {expanded && hasMore && (
          <div className="mt-1.5 flex flex-col gap-1">
            {(hasLocation || hasEmploymentType) && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <Maps.Map01 className="size-3 shrink-0 text-gray-400" />
                <span className="truncate">
                  {joinNonEmpty([item.employmentType, item.location], ' · ')}
                </span>
              </div>
            )}
            {hasDescription && (
              <p className="whitespace-pre-line text-xs text-gray-600 dark:text-gray-300">
                {item.description}
              </p>
            )}
            {hasCompanyUrl && (
              <ButtonA
                href={item.companyUrl ?? '#'}
                target="_blank"
                rel="noreferrer"
                size="2xs"
                text
                icon={<General.Link01 className="size-3" />}>
                Company page
              </ButtonA>
            )}
          </div>
        )}
      </div>
      {canDelete && confirmingDelete && (
        <DeletePopup
          onClose={() => setConfirmingDelete(false)}
          onDelete={() => {
            setConfirmingDelete(false);
            void onDelete();
          }}
        />
      )}
    </li>
  );
}

function EducationRow({ item }: { item: ContactEducationItem }) {
  const headline = item.school;
  const subline = joinNonEmpty([item.degree, item.fieldOfStudy], ', ');
  const dateRange = formatEducationDateRange(item.startDate, item.endDate);
  const meta = joinNonEmpty([dateRange, item.grade], ' · ');

  return (
    <li className="flex items-start gap-2.5">
      <Education.BookClosed className="mt-0.5 size-4 shrink-0 text-gray-400" />
      <div className="min-w-0 flex-1">
        {headline != null && (
          <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{headline}</div>
        )}
        {subline != null && (
          <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{subline}</div>
        )}
        {meta != null && (
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <Time.Calendar className="size-3 shrink-0 text-gray-400" />
            <span className="truncate">{meta}</span>
          </div>
        )}
        {item.activities != null && item.activities.trim().length > 0 && (
          <p className="mt-1 whitespace-pre-line text-xs text-gray-600 dark:text-gray-300">
            {item.activities}
          </p>
        )}
        {item.description != null && item.description.trim().length > 0 && (
          <p className="mt-1 whitespace-pre-line text-xs text-gray-600 dark:text-gray-300">
            {item.description}
          </p>
        )}
      </div>
    </li>
  );
}

export function ContactEmploymentEducationSectionPure({
  experiences,
  education,
  isLoading,
  onAddExperience,
  orgSearch,
  onDeleteExperience,
  contactFirstName,
}: ContactEmploymentEducationSectionPureProps) {
  const [isAddOpen, setIsAddOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
        <WaveSpinner />
      </div>
    );
  }

  const hasExperience = experiences.length > 0;
  const hasEducation = education.length > 0;
  const canAddExperience = onAddExperience != null && orgSearch != null;

  return (
    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
      <div>
        <div className="mb-2 flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1.5">
            <Maps.Map01 className="size-3.5 text-gray-400" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Experience</span>
          </div>
          {canAddExperience && (
            <Button
              size="2xs"
              text
              icon={<General.Plus />}
              tooltip="Add experience"
              onClick={() => setIsAddOpen(true)}>
              Add
            </Button>
          )}
        </div>
        {hasExperience && (
          <ul className="flex flex-col gap-3">
            {experiences.map((item) => (
              <ExperienceRow
                key={item.id}
                item={item}
                onDelete={onDeleteExperience ? () => onDeleteExperience(item.id) : undefined}
              />
            ))}
          </ul>
        )}
        {!hasExperience && (
          <p className="text-xs text-gray-500 dark:text-gray-400">No employment history found.</p>
        )}
      </div>
      <div className="my-3 border-t border-gray-100 dark:border-gray-800" />
      <div>
        <div className="mb-2 flex items-center gap-1.5">
          <Education.BookOpen01 className="size-3.5 text-gray-400" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Education</span>
        </div>
        {hasEducation && (
          <ul className="flex flex-col gap-3">
            {education.map((item) => (
              <EducationRow key={item.id} item={item} />
            ))}
          </ul>
        )}
        {!hasEducation && (
          <p className="text-xs text-gray-500 dark:text-gray-400">No education history found.</p>
        )}
      </div>
      {canAddExperience && isAddOpen && (
        <AddExperiencePopup
          onClose={() => setIsAddOpen(false)}
          onSubmit={async (input) => {
            await onAddExperience(input);
            setIsAddOpen(false);
          }}
          orgSearch={orgSearch}
          contactFirstName={contactFirstName}
        />
      )}
    </div>
  );
}
